import { SourceType } from '@atlas/types'
import { UploadDropzone } from '@atlas/ui-components'
import { useNavigate } from 'react-router-dom'
import type { WorkOrder } from '@/domain/work-order.types'
import { useThumbnails } from '@/hooks/useThumbnails'
import { downloadFile, useDeleteWorkOrderFile } from '@/hooks/useWorkOrderFiles'
import { sourceOwner, useUploadSource, useWorkOrderSources } from '@/hooks/useWorkOrderSources'
import { notification } from '@/notifications/notification-store'
import { DeliverableCard } from '../WorkOrders/DeliverableCard'
import { useSlotUploadDriver } from '../WorkOrders/workspace/sections/useSlotUploadDriver'

const ACCEPT = 'image/png,image/jpeg,application/pdf'
const MAX_BYTES = 50 * 1024 * 1024

const PACKAGE = SourceType.ORIGINAL_DISTRIBUTION_PACKAGE

/**
 * The client's distribution package files uploaded outside Veeva: source material the order
 * is made from, the counterpart of its original Veeva codes, never one of its deliverables.
 */
export const useDistributionPackageFiles = (orderCode: string) => useWorkOrderSources(orderCode, PACKAGE).data ?? []

/**
 * The distribution package files uploaded outside Veeva, and, for whoever may add to the
 * package, the drop zone to upload them and a delete on each.
 */
export function DistributionPackageUploads({ workOrder: wo, canEdit }: { workOrder: WorkOrder; canEdit: boolean }) {
	const files = useDistributionPackageFiles(wo.orderCode)
	// `discover` carries no presigned thumbnail, unlike the detail aggregate the deliverables read.
	const thumbnails = useThumbnails(files)
	const navigate = useNavigate()
	const upload = useUploadSource(wo.workOrderId, wo.orderCode, wo.tuple, PACKAGE)
	const del = useDeleteWorkOrderFile(wo.orderCode)
	const { rows, cardProps, uploadAll } = useSlotUploadDriver(
		sourceOwner(wo.orderCode, PACKAGE),
		upload.mutateAsync,
		files.map((file) => ({ ...file, thumbnailUrl: thumbnails[file.fileId] ?? undefined })),
	)

	const onFiles = (list: File[]) => {
		const tooBig = list.filter((file) => file.size > MAX_BYTES)
		for (const file of tooBig) notification.error(`${file.name} is over 50MB.`)
		void uploadAll(list.filter((file) => file.size <= MAX_BYTES))
	}

	return (
		<div className="space-y-2">
			{canEdit ? (
				<UploadDropzone
					label="Upload distribution package files"
					prompt="Drag & Drop your files here or browse"
					hint="PNG, JPG or PDF (Multiple files, up to 50MB each)"
					accept={ACCEPT}
					onFiles={onFiles}
				/>
			) : null}
			{rows.length ? (
				<ul className="space-y-2">
					{rows.map((row) => (
						<DeliverableCard
							key={row.key}
							{...cardProps(row)}
							onOpen={(id) => void navigate(`/assets/${id}`)}
							onDownload={(id) => void downloadFile(id)}
							onDelete={canEdit ? (id) => del.mutate(id) : undefined}
						/>
					))}
				</ul>
			) : null}
		</div>
	)
}
